
import { supabase } from '@/integrations/supabase/client';
import { AuthResult } from '@/types/auth.types';
import { cleanupAuthState } from '@/utils/authUtils';
import { createRateLimiter, logSecurityEvent, validateAndSanitizeInput } from '@/utils/security-utils';

// Define ToastOptions type locally since it's not exported from the toast component
interface ToastOptions {
  title?: string;
  description?: string;
  variant?: "default" | "destructive";
}

// Rate limiters for auth operations
const signInLimiter = createRateLimiter(5, 900000); // 5 attempts per 15 minutes
const signUpLimiter = createRateLimiter(3, 3600000); // 3 attempts per hour

export const authService = {
  // Iniciar sesión con validación y rate limiting
  async signIn(email: string, password: string): Promise<AuthResult & { toastOptions?: ToastOptions }> {
    try {
      console.log('Iniciando proceso de login...');

      // Input validation and sanitization
      const emailValidation = validateAndSanitizeInput(email, 'email', 255);
      if (!emailValidation.isValid) {
        return {
          success: false,
          error: emailValidation.error,
          toastOptions: {
            title: 'Error de validación',
            description: emailValidation.error || 'Email inválido',
            variant: 'destructive',
          }
        };
      }

      // Rate limiting check
      const clientId = `signin_${emailValidation.sanitizedValue}`;
      if (!signInLimiter.canProceed(clientId)) {
        logSecurityEvent('signin_rate_limited', {
          email: emailValidation.sanitizedValue,
          remainingAttempts: signInLimiter.getRemainingAttempts(clientId)
        });
        
        return {
          success: false,
          error: 'Demasiados intentos de inicio de sesión',
          toastOptions: {
            title: 'Demasiados intentos',
            description: `Has excedido el límite de intentos. Intenta de nuevo en 15 minutos.`,
            variant: 'destructive',
          }
        };
      }
      
      // Limpiar estado de autenticación existente
      cleanupAuthState();
      
      // Intentar cerrar sesión global (por si acaso)
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        console.log('Error al cerrar sesión global (continuando):', err);
      }
      
      console.log('Intentando autenticar con Supabase...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email: emailValidation.sanitizedValue,
        password,
      });

      if (error) {
        console.error('Error de Supabase en login:', error);
        
        // Log failed attempt
        logSecurityEvent('signin_failed', {
          email: emailValidation.sanitizedValue,
          error: error.message,
          remainingAttempts: signInLimiter.getRemainingAttempts(clientId)
        });

        let errorMessage = 'Error desconocido al iniciar sesión';
        
        // Manejar errores específicos
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Credenciales incorrectas. Verifica tu email y contraseña.';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Por favor confirma tu email antes de iniciar sesión.';
        } else if (error.message.includes('fetch')) {
          errorMessage = 'Problema de conexión. Verifica tu conexión a internet e intenta nuevamente.';
        } else {
          errorMessage = error.message;
        }
        
        return { 
          success: false, 
          error: errorMessage,
          toastOptions: {
            title: 'Error al iniciar sesión',
            description: errorMessage,
            variant: 'destructive',
          }
        };
      }

      // Log successful signin
      if (data.user) {
        logSecurityEvent('signin_success', {
          userId: data.user.id,
          email: emailValidation.sanitizedValue
        });
      }

      console.log('Login exitoso:', data);
      return { 
        success: true,
        toastOptions: {
          title: 'Inicio de sesión exitoso',
          description: '¡Bienvenido de nuevo!',
        }
      };
    } catch (error: any) {
      console.error('Error inesperado en login:', error);
      
      logSecurityEvent('signin_exception', {
        email: email,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      let errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'No se puede conectar al servidor. Verifica tu conexión a internet.';
      }
      
      return { 
        success: false, 
        error: errorMessage,
        toastOptions: {
          title: 'Error de conexión',
          description: errorMessage,
          variant: 'destructive',
        }
      };
    }
  },

  // Registrar nuevo usuario con validación mejorada
  async signUp(email: string, password: string): Promise<AuthResult & { toastOptions?: ToastOptions }> {
    try {
      console.log('Iniciando proceso de registro...');

      // Input validation and sanitization
      const emailValidation = validateAndSanitizeInput(email, 'email', 255);
      if (!emailValidation.isValid) {
        return {
          success: false,
          error: emailValidation.error,
          toastOptions: {
            title: 'Error de validación',
            description: emailValidation.error || 'Email inválido',
            variant: 'destructive',
          }
        };
      }

      // Password strength validation
      if (!password || password.length < 8) {
        return {
          success: false,
          error: 'Contraseña debe tener al menos 8 caracteres',
          toastOptions: {
            title: 'Contraseña débil',
            description: 'La contraseña debe tener al menos 8 caracteres para mayor seguridad.',
            variant: 'destructive',
          }
        };
      }

      // Rate limiting check
      const clientId = `signup_${emailValidation.sanitizedValue}`;
      if (!signUpLimiter.canProceed(clientId)) {
        logSecurityEvent('signup_rate_limited', {
          email: emailValidation.sanitizedValue,
          remainingAttempts: signUpLimiter.getRemainingAttempts(clientId)
        });
        
        return {
          success: false,
          error: 'Demasiados intentos de registro',
          toastOptions: {
            title: 'Demasiados intentos',
            description: `Has excedido el límite de intentos. Intenta de nuevo en 1 hora.`,
            variant: 'destructive',
          }
        };
      }
      
      // Limpiar estado de autenticación existente
      cleanupAuthState();
      
      console.log('Intentando registrar con Supabase...');
      const { data, error } = await supabase.auth.signUp({
        email: emailValidation.sanitizedValue,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) {
        console.error('Error de Supabase en registro:', error);
        
        // Log failed attempt
        logSecurityEvent('signup_failed', {
          email: emailValidation.sanitizedValue,
          error: error.message,
          remainingAttempts: signUpLimiter.getRemainingAttempts(clientId)
        });

        let errorMessage = 'Error desconocido al registrarse';
        
        // Manejar errores específicos
        if (error.message.includes('User already registered')) {
          errorMessage = 'Este email ya está registrado. Intenta iniciar sesión.';
        } else if (error.message.includes('Password should be at least')) {
          errorMessage = 'La contraseña debe tener al menos 6 caracteres.';
        } else if (error.message.includes('fetch')) {
          errorMessage = 'Problema de conexión. Verifica tu conexión a internet e intenta nuevamente.';
        } else {
          errorMessage = error.message;
        }
        
        return { 
          success: false, 
          error: errorMessage,
          toastOptions: {
            title: 'Error al registrarse',
            description: errorMessage,
            variant: 'destructive',
          }
        };
      }

      // Log successful signup
      if (data.user) {
        logSecurityEvent('signup_success', {
          userId: data.user.id,
          email: emailValidation.sanitizedValue
        });
      }

      console.log('Registro exitoso:', data);
      return { 
        success: true,
        toastOptions: {
          title: 'Registro exitoso',
          description: 'Por favor, verifica tu correo electrónico para confirmar tu cuenta.',
        }
      };
    } catch (error: any) {
      console.error('Error inesperado en registro:', error);
      
      logSecurityEvent('signup_exception', {
        email: email,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      let errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'No se puede conectar al servidor. Verifica tu conexión a internet.';
      }
      
      return { 
        success: false, 
        error: errorMessage,
        toastOptions: {
          title: 'Error de conexión',
          description: errorMessage,
          variant: 'destructive',
        }
      };
    }
  },

  // Cerrar sesión con logging de seguridad
  async signOut(): Promise<{ toastOptions?: ToastOptions }> {
    try {
      console.log('Iniciando proceso de logout...');
      
      // Log signout attempt
      logSecurityEvent('signout_attempt', {
        timestamp: new Date().toISOString()
      });
      
      // Limpiar estado de autenticación
      cleanupAuthState();
      
      // Intentar cerrar sesión global
      await supabase.auth.signOut({ scope: 'global' });
      
      // Log successful signout
      logSecurityEvent('signout_success', {
        timestamp: new Date().toISOString()
      });
      
      console.log('Logout exitoso');
      return {
        toastOptions: {
          title: 'Sesión cerrada',
          description: 'Has cerrado sesión correctamente.',
        }
      };
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      
      logSecurityEvent('signout_failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });

      return {
        toastOptions: {
          title: 'Error',
          description: 'Ocurrió un error al cerrar sesión',
          variant: 'destructive',
        }
      };
    }
  },

  // Obtener la sesión actual
  async getSession() {
    try {
      console.log('Obteniendo sesión actual...');
      const result = await supabase.auth.getSession();
      console.log('Sesión obtenida:', result);
      return result;
    } catch (error) {
      console.error('Error al obtener sesión:', error);
      throw error;
    }
  }
};
